import { useEffect, useState } from 'react'
import {
  Stack,
  TableContainer,
  TableHead,
  TableRow,
  TableCell,
  Table,
  TableBody,
  styled,
  tableCellClasses,
  Typography,
  TextField,
  MenuItem,
  Select,
  Divider,
  Box,
  Button,
} from '@mui/material'
import { CollapsibleSection } from 'components/CollapsibleSection'
import {
  useFieldArray,
  useForm,
  Controller,
  FormProvider,
  useFormContext,
} from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import {
  useCreateDocumentSubmissionMutation,
  useSubmissionQuery,
} from 'services/submissions'
import {
  AnswerExistance,
  AnswerExistanceOptions,
  DocumentSubmissionRequirement,
  mapDocumentSubmissionToPayload,
  mapToDefaultValues,
} from './lots/map-to-form-values'
import { Loading } from 'components/Loading'
import { DownloadFile } from 'components/DownloadFile'
import { ILot } from 'types/lots'
import { ISubmissionOverview, SubmissionStatus } from 'types/submissions'
import { formatTimeStampToDate } from 'hooks/date/formattedTime'
import { toast } from 'react-toastify'

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
  },
}))

type Props = {
  submissionId: string
  lot: ILot
  submissionItem: ISubmissionOverview
}

type FormValues = {
  documentRequirements: DocumentSubmissionRequirement[]
}

export const RequiredDocsSubmissionItem = ({
  submissionId,
  submissionItem,
  lot,
}: Props) => {
  const { t } = useTranslation()
  const [expanded, setExpanded] = useState<boolean>(false)

  const { data: submission, isLoading } = useSubmissionQuery(
    { submissionId },
    { skip: !submissionId && !expanded }
  )

  const methods = useForm<FormValues>({
    mode: 'onChange',
    defaultValues: {
      documentRequirements: [],
    },
  })

  const { control, reset, setValue, handleSubmit } = methods

  const { fields } = useFieldArray({
    control,
    name: `documentRequirements`,
  })

  useEffect(() => {
    if (lot && submission) {
      const mappedValues = mapToDefaultValues({
        documentRequirements: lot.documentRequirements,
        documentSubmissions: submission.documentSubmissions,
      })

      reset({ documentRequirements: mappedValues })
    }
  }, [lot, submission, reset, setValue])

  const handleChangeAccordion = (expanded: boolean) => {
    setExpanded(expanded)
  }

  const [createDocSubmission, { isSuccess, isError, isUninitialized }] =
    useCreateDocumentSubmissionMutation()

  const onSubmit = async ({ documentRequirements }: FormValues) => {
    const body = mapDocumentSubmissionToPayload(documentRequirements, submission!)
    try {
      const promises: any =
        body.documentSubmissions &&
        body.documentSubmissions.map((item) => createDocSubmission(item).unwrap())
      const results = await Promise.all(promises)
    } catch (error) {
      console.error('Error submitting documents:', error)
    }
  }

  useEffect(() => {
    if (isSuccess) {
      toast.success(t('operation-success'))
    }
    if (isError) {
      toast.error(t('failedToCreate'))
    }
  }, [isSuccess, isError, t])

  if (isLoading) {
    return <Loading fullPage={false} />
  }
  if (
    submission === undefined ||
    lot === undefined ||
    submissionItem === undefined
  ) {
    return <></>
  }

  return (
    <Box
      sx={{
        marginTop: '10px',
      }}
    >
      <Stack spacing={1}>
        <CollapsibleSection
          variant={'secondary'}
          title={submissionItem.submitterOrganizationName ?? ''}
          additionalTitle={
            <Box sx={{ display: 'flex', gap: '20px', marginRight: '30px' }}>
              <Typography>
                {t('status')} -{' '}
                {submissionItem.status
                  ? t(`submission-status-${SubmissionStatus[submissionItem.status]}`)
                  : ''}
              </Typography>
              <Typography>
                {t('submission-presentation-date')} -{' '}
                {formatTimeStampToDate(submissionItem.submittedAt)}
              </Typography>
            </Box>
          }
          defaultExpanded={false}
          onChange={(event: React.SyntheticEvent, expanded: boolean) => {
            handleChangeAccordion(expanded)
          }}
        >
          <FormProvider {...methods}>
            <form onSubmit={handleSubmit(onSubmit)}>
              <CollapsibleSection title={t('legal-docs')}>
                <Stack>
                  <TableContainer>
                    <Table
                      sx={{
                        tableLayout: 'fixed',
                        '& td, th': {
                          border: '1px solid #ccc',
                        },
                      }}
                    >
                      <TableHead>
                        <TableRow>
                          <StyledTableCell>
                            {t('evaluationOfDocuments')}
                          </StyledTableCell>
                          <StyledTableCell>
                            {t('contractorOfferAndNotes')}
                          </StyledTableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {fields[0]?.documentRequirementItems.map((item, index) => (
                          <TableRow key={item.id}>
                            <TableCell>
                              <RequiredDocsSubmissionLeftColumn
                                outerIndex={0}
                                index={index}
                              />
                            </TableCell>
                            <TableCell sx={{ verticalAlign: 'top' }}>
                              <RequiredDocsSubmissionRightColumn
                                outerIndex={0}
                                index={index}
                              />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Stack>
              </CollapsibleSection>
              <CollapsibleSection title={t('speciality')}>
                <Stack>
                  <TableContainer>
                    <Table
                      sx={{
                        tableLayout: 'fixed',
                        '& td, th': {
                          border: '1px solid #ccc',
                        },
                      }}
                    >
                      <TableHead>
                        <TableRow>
                          <StyledTableCell>
                            {t('evaluationOfDocuments')}
                          </StyledTableCell>
                          <StyledTableCell>
                            {t('contractorOfferAndNotes')}
                          </StyledTableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {fields[1]?.documentRequirementItems.map((item, index) => (
                          <TableRow key={item.id}>
                            <TableCell>
                              <RequiredDocsSubmissionLeftColumn
                                outerIndex={1}
                                index={index}
                              />
                            </TableCell>
                            <TableCell sx={{ verticalAlign: 'top' }}>
                              <RequiredDocsSubmissionRightColumn
                                outerIndex={1}
                                index={index}
                              />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Stack>
              </CollapsibleSection>
              <Stack direction="row" justifyContent="flex-end">
                <Button
                  size="small"
                  variant="contained"
                  style={{ color: '#fff', backgroundColor: 'rgb(3, 34, 76)' }}
                  onClick={handleSubmit(onSubmit)}
                  // disabled={
                  //   !isValid || (!methods.formState.isDirty && isUninitialized)
                  // }
                >
                  {t('save')}
                </Button>
              </Stack>
            </form>
          </FormProvider>
        </CollapsibleSection>
      </Stack>
    </Box>
  )
}

const RequiredDocsSubmissionLeftColumn = ({
  outerIndex,
  index,
}: {
  outerIndex: number
  index: number
}) => {
  const { t } = useTranslation()

  const { control } = useFormContext()

  return (
    <Stack spacing={1.5}>
      <Typography>{`${t('requirement')}: ${index + 1}`}</Typography>
      <Controller
        control={control}
        name={`documentRequirements.${outerIndex}.documentRequirementItems.${index}.criteria`}
        render={({ field }) => (
          <TextField
            {...field}
            InputProps={{
              rows: 2,
              multiline: true,
              inputComponent: 'textarea',
            }}
            fullWidth
            label={t('requirements')}
            disabled
            variant="outlined"
          />
        )}
      />
      <Controller
        control={control}
        name={`documentRequirements.${outerIndex}.documentRequirementItems.${index}.eligibility`}
        render={({ field }) => (
          <TextField
            {...field}
            InputProps={{
              rows: 2,
              multiline: true,
              inputComponent: 'textarea',
            }}
            fullWidth
            label={t('evaluationMethod')}
            disabled
            variant="outlined"
          />
        )}
      />
    </Stack>
  )
}

const RequiredDocsSubmissionRightColumn = ({
  outerIndex,
  index,
}: {
  outerIndex: number
  index: number
}) => {
  const { t } = useTranslation()

  const { control, watch } = useFormContext()

  const exist = watch(
    `documentRequirements.${outerIndex}.documentRequirementItems.${index}.exist`
  )

  const isDisabled = exist !== AnswerExistance.EXIST

  return (
    <Stack spacing={1.5}>
      <Stack spacing={1.5}>
        <Typography>{`${t('answer')}: ${index + 1}`}</Typography>
        <Controller
          control={control}
          name={`documentRequirements.${outerIndex}.documentRequirementItems.${index}.exist`}
          disabled
          render={({ field }) => (
            <Select {...field} size="small">
              {AnswerExistanceOptions.map((existence) => {
                return (
                  <MenuItem key={existence} value={existence}>
                    {t(`existence.${existence}`)}
                  </MenuItem>
                )
              })}
            </Select>
          )}
        />
      </Stack>
      <Controller
        control={control}
        name={`documentRequirements.${outerIndex}.documentRequirementItems.${index}.note`}
        render={({ field }) => (
          <TextField
            {...field}
            InputProps={{
              rows: 2,
              multiline: true,
              inputComponent: 'textarea',
            }}
            fullWidth
            label={t('notes')}
            required
            disabled
            variant="outlined"
          />
        )}
      />

      <Controller
        name={`documentRequirements.${outerIndex}.documentRequirementItems.${index}.evaluationNote`}
        control={control}
        defaultValue={`documentRequirements.${outerIndex}.documentRequirementItems.${index}.evaluationNote`}
        render={({ field }) => (
          <TextField
            {...field}
            fullWidth
            label={t('procurement-specialist-comment')}
            InputProps={{
              rows: 2,
              multiline: true,
              inputComponent: 'textarea',
            }}
          />
        )}
      />
      <Controller
        name={`documentRequirements.${outerIndex}.documentRequirementItems.${index}.submittedDocumentFileId`}
        control={control}
        render={({ field }) => <DownloadFile fileId={field.value} />}
      />
    </Stack>
  )
}
